import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ObjectPhoto } from '../../components/shared/ObjectPhoto';
import { ApiError } from '../../lib/api';
import {
  dictionariesApi,
  type DictionaryDetail,
  type DictionaryFieldDetail,
  type DictionaryValue,
} from '../../lib/dictionariesApi';

function formatValue(field: DictionaryFieldDetail, raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  if (field.data_type === 'boolean') return raw ? 'да' : 'нет';
  if (field.data_type === 'integer' || field.data_type === 'decimal') {
    const n = Number(raw);
    return Number.isNaN(n) ? String(raw) : n.toLocaleString('ru-RU');
  }
  if (field.data_type === 'date') return new Date(String(raw)).toLocaleDateString('ru-RU');
  if (field.data_type === 'datetime') return new Date(String(raw)).toLocaleString('ru-RU');
  return String(raw);
}

export function DictionaryValueCardPage() {
  const { dictionaryId, valueId } = useParams<{ dictionaryId: string; valueId: string }>();

  const [dictionary, setDictionary] = useState<DictionaryDetail | null>(null);
  const [value, setValue] = useState<DictionaryValue | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [properties, setProperties] = useState<Record<string, unknown>>({});
  const [editingCode, setEditingCode] = useState<string | 'name' | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!dictionaryId || !valueId) return;
    setLoading(true);
    Promise.all([dictionariesApi.getDictionary(dictionaryId), dictionariesApi.getValue(dictionaryId, valueId)])
      .then(([dict, val]) => {
        setDictionary(dict);
        setValue(val);
        setName(val.name);
        setProperties(val.properties);
      })
      .catch((err) => setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить карточку.'))
      .finally(() => setLoading(false));
  }, [dictionaryId, valueId]);

  const commitName = async () => {
    setEditingCode(null);
    const trimmed = editDraft.trim();
    if (!dictionaryId || !valueId || !trimmed || trimmed === name) return;
    try {
      const updated = await dictionariesApi.updateValue(dictionaryId, valueId, trimmed, properties);
      setValue(updated);
      setName(updated.name);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить название.');
    }
  };

  const commitField = async (field: DictionaryFieldDetail, raw: string) => {
    setEditingCode(null);
    if (!dictionaryId || !valueId) return;
    let next: unknown = raw.trim() === '' ? null : raw;
    if (next !== null && field.data_type === 'integer') {
      if (!/^-?\d+$/.test(raw.trim())) {
        setSaveError('Нужно целое число');
        return;
      }
      next = Number(raw.trim());
    } else if (next !== null && field.data_type === 'decimal') {
      const n = Number(raw.trim());
      if (Number.isNaN(n)) {
        setSaveError('Нужно число');
        return;
      }
      next = n;
    }
    const nextProps = { ...properties, [field.code]: next };
    try {
      const updated = await dictionariesApi.updateValue(dictionaryId, valueId, name, nextProps);
      setValue(updated);
      setProperties(updated.properties);
      setSaveError(null);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить значение.');
    }
  };

  const handleSetPhoto = async (fileId: string) => {
    if (!dictionaryId || !valueId) return;
    try {
      const updated = await dictionariesApi.setValuePhoto(dictionaryId, valueId, fileId);
      setValue(updated);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить фото.');
    }
  };

  const toggleBoolean = async (field: DictionaryFieldDetail, checked: boolean) => {
    if (!dictionaryId || !valueId) return;
    const nextProps = { ...properties, [field.code]: checked };
    try {
      const updated = await dictionariesApi.updateValue(dictionaryId, valueId, name, nextProps);
      setValue(updated);
      setProperties(updated.properties);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Не удалось сохранить значение.');
    }
  };

  if (loading) return <p className="p-8 text-sm text-text-secondary">Загрузка…</p>;
  if (loadError || !dictionary || !value) {
    return <p className="p-8 text-sm font-medium text-status-danger">{loadError ?? 'Значение не найдено.'}</p>;
  }

  return (
    <div className="flex h-screen w-full flex-col overflow-y-auto bg-bg-canvas">
      <header className="flex h-[72px] shrink-0 items-center gap-4 border-b border-border-default bg-bg-surface px-8">
        <Link to={`/app/dictionaries/${dictionaryId}`} className="text-sm font-medium text-text-secondary hover:text-text-primary">
          ← {dictionary.name}
        </Link>
        <h1 className="text-[17px] font-semibold text-text-primary">{name}</h1>
      </header>

      <div className="flex-1 px-8 py-8">
        <div className="mx-auto max-w-[640px] rounded-2xl border border-border-default bg-bg-surface p-6">
          <div className="flex justify-center">
            <ObjectPhoto fileId={value.photo_file_id} canEdit size={160} onChange={handleSetPhoto} />
          </div>

          <label className="mt-5 block text-[11px] font-medium text-text-secondary">Название</label>
          {editingCode === 'name' ? (
            <input
              autoFocus
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => e.key === 'Enter' && commitName()}
              className="mt-1.5 h-10 w-full rounded-[9px] border border-action-primary bg-bg-surface px-3 text-[15px] font-semibold text-text-primary outline-none"
            />
          ) : (
            <p
              onClick={() => {
                setEditingCode('name');
                setEditDraft(name);
              }}
              className="mt-1.5 cursor-pointer rounded-[9px] px-3 py-2 text-[15px] font-semibold text-text-primary hover:bg-bg-surface-secondary"
            >
              {name}
            </p>
          )}

          {dictionary.fields.length > 0 && <div className="my-4 h-px bg-border-default" />}

          <div className="flex flex-col gap-3">
            {dictionary.fields.map((field) => (
              <div key={field.code}>
                <label className="text-[11px] font-medium text-text-secondary">{field.name}</label>
                {field.data_type === 'boolean' ? (
                  <div className="mt-1.5">
                    <input
                      type="checkbox"
                      checked={Boolean(properties[field.code])}
                      onChange={(e) => toggleBoolean(field, e.target.checked)}
                      className="size-[18px] rounded-[5px] accent-action-primary"
                    />
                  </div>
                ) : editingCode === field.code ? (
                  <input
                    autoFocus
                    type={field.data_type === 'date' ? 'date' : field.data_type === 'datetime' ? 'datetime-local' : field.data_type === 'integer' || field.data_type === 'decimal' ? 'number' : 'text'}
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onBlur={() => commitField(field, editDraft)}
                    onKeyDown={(e) => e.key === 'Enter' && commitField(field, editDraft)}
                    className="mt-1.5 h-10 w-full rounded-[9px] border border-action-primary bg-bg-surface px-3 text-[13px] text-text-primary outline-none"
                  />
                ) : (
                  <p
                    onClick={() => {
                      setEditingCode(field.code);
                      const raw = properties[field.code];
                      setEditDraft(raw === null || raw === undefined ? '' : String(raw));
                    }}
                    className="mt-1.5 cursor-pointer rounded-[9px] px-3 py-2 text-[13px] text-text-primary hover:bg-bg-surface-secondary"
                  >
                    {formatValue(field, properties[field.code])}
                  </p>
                )}
              </div>
            ))}
          </div>

          {saveError && <p className="mt-4 text-xs font-medium text-status-danger">{saveError}</p>}
        </div>
      </div>
    </div>
  );
}
